/**
 * The clock and the subscription behind `photoRevealGate` — the group's photographs held until the
 * first arrival's window closes, then shown together.
 *
 * Same split as `loadingGate` / `useSkeletonGate` beside it: the policy is pure and tested, this
 * owns the timer and the one piece of state, and decides nothing.
 *
 * ── What a caller does with it ───────────────────────────────────────────────────────────────
 * One call per GROUP — a list, or a single hero — with the number of photographs that group draws.
 * Every `<img>` in it carries `holding` while the window is open, `reveal` once it has closed, and
 * calls `onPhotoSettled` from both `onLoad` and `onError`. A failure is an arrival: a cover that
 * 404s must open the window like any other, or one broken file would hold a whole screenful grey.
 *
 * ── Why the count is a parameter rather than a registration ──────────────────────────────────
 * The group only needs to know whether it draws ANY photograph, and a list already knows how many
 * of its rows have a cover. Having each image register itself on mount would buy the same fact at
 * the price of a shared context, a render per row and an ordering problem on the first frame.
 *
 * ── The group has an IDENTITY, and forgetting it was the first defect here ───────────────────
 * A window that has closed stays closed, which is right for a straggler and wrong for a new set of
 * photographs. The plans list renders ONE element for both of its scopes, so switching from the
 * templates to the clients' plans replaced every cover in the list while the hook instance
 * survived — and the covers dripped in one at a time again, which is the whole defect this exists
 * to fix. So the caller names the group; a new name re-arms the window.
 *
 * ── One boolean of state, one re-render ──────────────────────────────────────────────────────
 * The first arrival's timestamp is the wait's MEMORY and lives in a ref: nothing renders from it,
 * and re-rendering a hundred rows on every cover that lands is exactly the churn this hook exists
 * to avoid. `revealed` is the one thing a render reads, it changes once, and the whole group
 * appears on that single pass. Writing a ref from an EFFECT is allowed under the React Compiler;
 * writing one during render is not, which is why nothing below touches it in the body.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import photoBox from '../ui/photoBox.module.css';
import skel from '../ui/skeleton.module.css';

import { photoRevealGate } from './photoRevealGate';

/** What a surface needs to draw one group of photographs. */
export interface PhotoRevealGroup {
  /**
   * How many photographs this group draws. Zero reveals immediately — there is nothing to hold, and
   * a caller must never be handed a holding class it will not be told to drop.
   */
  readonly photoCount: number;
  /**
   * What makes this a DIFFERENT set of photographs. A change re-arms the window, so a list that
   * swaps its rows for other rows collects the new covers rather than dripping them in behind a
   * window that closed for the previous set.
   *
   * It is a plain string on purpose: whatever identifies the set to its own surface — a scope name,
   * a client's id, a filter's signature. What it must NOT be is something that changes while the
   * same photographs are on screen, which would put them back into grey.
   */
  readonly groupKey: string;
}

export interface PhotoReveal {
  /**
   * The class every `<img>` in the group carries while the window is open — `photoBox.holding`, or
   * `''` once the group is shown. It takes the picture out of sight and leaves the box and its
   * grey ground exactly where they were.
   */
  readonly holding: string;
  /**
   * The class those same images carry once the group is revealed — `skel.reveal`, the app's 120ms
   * hand-over, so the group arrives rather than snaps. The same class the skeleton gate gives the
   * content that replaces its bars, because it is the same moment.
   */
  readonly reveal: string;
  /** Called from an image's `onLoad` AND its `onError`. Cheap, and safe to call many times. */
  readonly onPhotoSettled: () => void;
}

export function usePhotoReveal({ photoCount, groupKey }: PhotoRevealGroup): PhotoReveal {
  const wait = useRef<{ groupKey: string; firstSettledAt: number | null }>({
    groupKey,
    firstSettledAt: null,
  });
  const [revealed, setRevealed] = useState(() => photoCount === 0);
  /* Bumped by the first settle only, to wake the effect below. The timestamp itself is a ref, so
   * without this the effect would have nothing to depend on and the window would never be timed. */
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // A new set of photographs starts a new wait. Done in the effect rather than during render,
    // which is where the React Compiler allows a ref to be written.
    if (wait.current.groupKey !== groupKey) {
      wait.current = { groupKey, firstSettledAt: null };
    }

    const step = () => {
      const now = performance.now();
      const verdict = photoRevealGate({
        photoCount,
        firstSettledAt: wait.current.firstSettledAt,
        now,
      });
      setRevealed(verdict.revealed);
      if (verdict.recheckAt !== null) {
        timer = setTimeout(step, Math.max(0, verdict.recheckAt - now));
      }
    };

    step();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [photoCount, groupKey, opened]);

  /* Stable across renders so an `<img>` does not get a new handler every pass. A ref written from
   * an EVENT HANDLER is allowed under the React Compiler, as is one written from an effect; what is
   * forbidden is writing one during render, and neither site here does. */
  const onPhotoSettled = useCallback(() => {
    // Only the FIRST arrival matters: it is what opens the window, and every later one inside the
    // window is the reason the window exists. A second write would push the group's appearance
    // back by one more cover each time, which is the drip this replaces.
    if (wait.current.firstSettledAt !== null) return;
    wait.current = { ...wait.current, firstSettledAt: performance.now() };
    setOpened((open) => !open);
  }, []);

  return {
    holding: revealed ? '' : String(photoBox.holding),
    reveal: revealed ? String(skel.reveal) : '',
    onPhotoSettled,
  };
}
